import React, { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../../services/api';
import { useDebouncedSearch } from '../../hooks/useDebouncedSearch';

/**
 * Member-card account panel (Phase 2). Views a Bridge Club member's account
 * ledger — dollar credit, bag grants, and open tab — and adds entries. Every
 * action posts to /admin/bridge-club/members/<id>/account/* and the endpoint
 * returns the refreshed panel payload, which we set in place. The tab-limit
 * control is shown only when the server reports can_edit_tab_limit (managers+);
 * the server enforces it regardless.
 */

interface MemberLite {
  id: number;
  first_name: string;
  last_name: string;
  member_number?: string;
}

interface LedgerEntry {
  id: number;
  entry_type: string;
  entry_type_display: string;
  amount: string | null;
  quantity: number | null;
  bag_grant: number | null;
  note: string;
  created_at: string;
  created_by_name: string | null;
}

interface BagGrant {
  id: number;
  scope_type: string;
  scope_type_display: string;
  product_name: string | null;
  product_category_name: string | null;
  products_detail: { id: number; name: string }[];
  granted_quantity: number;
  remaining_quantity: number;
  reason: string;
  is_active: boolean;
}

interface AccountData {
  customer_id: number;
  dollar_credit_balance: string;
  tab_balance: string;
  tab_limit: { effective: string | null; override: string | null; is_default: boolean };
  can_edit_tab_limit: boolean;
  bag_grants: BagGrant[];
  ledger: LedgerEntry[];
}

interface ProductOption { id: number; name: string; sku: string }
interface CategoryOption { id: number; name: string }

type FormKind = 'credit' | 'bag' | 'tabCharge' | 'tabPayment' | 'adjust' | 'tabLimit' | null;
type BagScope = 'product' | 'category' | 'product_set';

interface Props {
  member: MemberLite;
  onClose: () => void;
}

const parseErr = (err: any): string => {
  const data = err?.response?.data;
  if (!data) return 'Something went wrong. Please try again.';
  if (typeof data === 'string') return data;
  if (data.error) return data.error;
  const first = Object.values(data)[0];
  if (Array.isArray(first)) return String(first[0]);
  return String(first ?? 'Request failed.');
};

const fmtDate = (iso: string): string => {
  try {
    return new Date(iso).toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

const asArray = (payload: any): any[] =>
  Array.isArray(payload) ? payload : Array.isArray(payload?.results) ? payload.results : [];

const MemberAccountModal: React.FC<Props> = ({ member, onClose }) => {
  const [data, setData] = useState<AccountData | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [activeForm, setActiveForm] = useState<FormKind>(null);

  const [products, setProducts] = useState<ProductOption[]>([]);
  const [categories, setCategories] = useState<CategoryOption[]>([]);

  // Form fields
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [reason, setReason] = useState('');
  const [limitValue, setLimitValue] = useState('');
  const [bagScope, setBagScope] = useState<BagScope>('product');
  const [bagProduct, setBagProduct] = useState('');
  const [bagCategory, setBagCategory] = useState('');
  const [bagProducts, setBagProducts] = useState<number[]>([]);
  const [bagGranted, setBagGranted] = useState('');
  const [bagAlreadyPulled, setBagAlreadyPulled] = useState('');

  // Client-side search for the "chosen set of products" checkbox picker. The
  // list is already loaded; the debounced hook keeps the box responsive while a
  // large catalog filters. 250ms — in-memory filter guidance.
  const bagSearch = useDebouncedSearch('', 250);

  // Products matching the picker search (name or SKU); full list when empty.
  const bagProductMatches = useMemo(() => {
    const q = bagSearch.debounced.trim().toLowerCase();
    if (!q) return products;
    return products.filter(
      (p) => p.name.toLowerCase().includes(q) || p.sku.toLowerCase().includes(q),
    );
  }, [products, bagSearch.debounced]);

  // Checked products, pinned so a search never hides prior picks; and the
  // unselected matches shown in the searchable list below the pinned block.
  const selectedBagProducts = useMemo(
    () => products.filter((p) => bagProducts.includes(p.id)),
    [products, bagProducts],
  );
  const unselectedBagMatches = useMemo(
    () => bagProductMatches.filter((p) => !bagProducts.includes(p.id)),
    [bagProductMatches, bagProducts],
  );

  const base = `/admin/bridge-club/members/${member.id}/account/`;

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const [acct, prods, cats] = await Promise.all([
        api.get(base),
        api.get('/store/products/'),
        api.get('/store/categories/'),
      ]);
      setData(acct.data);
      setProducts(asArray(prods.data).map((p: any) => ({ id: p.id, name: p.name, sku: p.sku || '' })));
      setCategories(asArray(cats.data).map((c: any) => ({ id: c.id, name: c.name })));
    } catch (err) {
      setLoadError(parseErr(err));
    } finally {
      setLoading(false);
    }
  }, [base]);

  useEffect(() => { load(); }, [load]);

  const resetForms = () => {
    setAmount(''); setNote(''); setReason(''); setLimitValue('');
    setBagScope('product'); setBagProduct(''); setBagCategory('');
    setBagProducts([]); setBagGranted(''); setBagAlreadyPulled('');
    bagSearch.setValue('');
    setActionError(null);
  };

  const openForm = (kind: FormKind) => {
    resetForms();
    setActiveForm(kind);
  };

  const submit = async (suffix: string, body: any) => {
    setSubmitting(true);
    setActionError(null);
    try {
      const res = await api.post(`${base}${suffix}`, body);
      setData(res.data);
      resetForms();
      setActiveForm(null);
    } catch (err) {
      setActionError(parseErr(err));
    } finally {
      setSubmitting(false);
    }
  };

  const toggleBagProduct = (id: number) => {
    setBagProducts((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const submitBag = () => {
    const body: any = {
      scope_type: bagScope,
      granted_quantity: bagGranted,
      already_pulled: bagAlreadyPulled || 0,
      note,
    };
    if (bagScope === 'product') body.product = bagProduct;
    else if (bagScope === 'category') body.product_category = bagCategory;
    else body.products = bagProducts;
    submit('bag-grant/', body);
  };

  const tabLimit = data?.tab_limit;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-content member-account-modal"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 760, width: '92%' }}
      >
        <div className="modal-header">
          <h2>Account — {member.first_name} {member.last_name}</h2>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>

        <div className="modal-body">
          {loading ? (
            <div>Loading account…</div>
          ) : loadError ? (
            <div className="error-message">{loadError}</div>
          ) : data ? (
            <>
              {/* Balances */}
              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 16 }}>
                <div style={{ flex: '1 1 200px', background: '#f5f5f5', borderRadius: 8, padding: 12 }}>
                  <div style={{ fontSize: 12, color: '#666' }}>Store credit</div>
                  <div style={{ fontSize: 22, fontWeight: 700 }}>${data.dollar_credit_balance}</div>
                </div>
                <div style={{ flex: '1 1 200px', background: '#f5f5f5', borderRadius: 8, padding: 12 }}>
                  <div style={{ fontSize: 12, color: '#666' }}>Open tab</div>
                  <div style={{ fontSize: 22, fontWeight: 700 }}>${data.tab_balance}</div>
                  <div style={{ fontSize: 12, color: '#666' }}>
                    Limit: {tabLimit?.effective ?? '—'}{tabLimit?.is_default ? ' (default)' : ''}
                  </div>
                </div>
              </div>

              {/* Bag grants */}
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontWeight: 600, marginBottom: 6 }}>Bag credit grants</div>
                {data.bag_grants.length === 0 ? (
                  <div style={{ color: '#888', fontSize: 14 }}>None.</div>
                ) : (
                  <ul style={{ margin: 0, paddingLeft: 18 }}>
                    {data.bag_grants.map((g) => (
                      <li key={g.id} style={{ marginBottom: 4 }}>
                        <strong>{g.remaining_quantity}</strong> of {g.granted_quantity} left —{' '}
                        {g.scope_type_display}:{' '}
                        {g.product_name || g.product_category_name ||
                          g.products_detail.map((p) => p.name).join(', ') || '—'}
                        {!g.is_active && <em style={{ color: '#b00' }}> (retired)</em>}
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {/* Action buttons */}
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
                <button className="action-btn edit-btn" onClick={() => openForm('credit')}
                  title="Add dollar store credit the member can spend on anything.">Add credit</button>
                <button className="action-btn edit-btn" onClick={() => openForm('bag')}
                  title="Give the member a count of a specific feed or category to pull against.">Grant bag credit</button>
                <button className="action-btn edit-btn" onClick={() => openForm('tabCharge')}
                  title="Add an amount the member owes to their open tab.">Tab charge</button>
                <button className="action-btn edit-btn" onClick={() => openForm('tabPayment')}
                  title="Record money the member paid toward their open tab.">Tab payment</button>
                <button className="action-btn edit-btn" onClick={() => openForm('adjust')}
                  title="Manually correct a balance, with a reason.">Adjustment</button>
                {data.can_edit_tab_limit && (
                  <button className="action-btn reset-password-btn" onClick={() => openForm('tabLimit')}
                    title="Set the ceiling on how high this member's tab can go (managers only).">
                    Set tab limit
                  </button>
                )}
              </div>

              {actionError && <div className="error-message" style={{ marginBottom: 8 }}>{actionError}</div>}

              {/* Forms */}
              {activeForm === 'credit' && (
                <div className="account-form">
                  <input type="number" step="0.01" placeholder="Amount" value={amount}
                    onChange={(e) => setAmount(e.target.value)} />
                  <input type="text" placeholder="Note (optional)" value={note}
                    onChange={(e) => setNote(e.target.value)} />
                  <button disabled={submitting} onClick={() => submit('credit/', { amount, note })}>Add</button>
                </div>
              )}

              {(activeForm === 'tabCharge' || activeForm === 'tabPayment') && (
                <div className="account-form">
                  <input type="number" step="0.01" placeholder="Amount" value={amount}
                    onChange={(e) => setAmount(e.target.value)} />
                  <input type="text" placeholder="Note (optional)" value={note}
                    onChange={(e) => setNote(e.target.value)} />
                  <button disabled={submitting}
                    onClick={() => submit(activeForm === 'tabCharge' ? 'tab-charge/' : 'tab-payment/', { amount, note })}>
                    {activeForm === 'tabCharge' ? 'Charge' : 'Record payment'}
                  </button>
                </div>
              )}

              {activeForm === 'adjust' && (
                <div className="account-form">
                  <input type="number" step="0.01" placeholder="Amount (+/−)" value={amount}
                    onChange={(e) => setAmount(e.target.value)} />
                  <input type="text" placeholder="Reason (required)" value={reason}
                    onChange={(e) => setReason(e.target.value)} />
                  <button disabled={submitting} onClick={() => submit('adjustment/', { amount, reason })}>Apply</button>
                </div>
              )}

              {activeForm === 'tabLimit' && data.can_edit_tab_limit && (
                <div className="account-form">
                  <input type="number" step="0.01" placeholder="New limit (blank = default)" value={limitValue}
                    onChange={(e) => setLimitValue(e.target.value)} />
                  <button disabled={submitting} onClick={() => submit('tab-limit/', { tab_limit: limitValue })}>
                    Save limit
                  </button>
                </div>
              )}

              {activeForm === 'bag' && (
                <div className="account-form" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 8 }}>
                  <select value={bagScope} onChange={(e) => setBagScope(e.target.value as BagScope)}>
                    <option value="product">Specific product</option>
                    <option value="category">Any product in a category</option>
                    <option value="product_set">A chosen set of products</option>
                  </select>

                  {bagScope === 'product' && (
                    <select value={bagProduct} onChange={(e) => setBagProduct(e.target.value)}>
                      <option value="">Select a product…</option>
                      {products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                  )}
                  {bagScope === 'category' && (
                    <select value={bagCategory} onChange={(e) => setBagCategory(e.target.value)}>
                      <option value="">Select a category…</option>
                      {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  )}
                  {bagScope === 'product_set' && (
                    <div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                        <span style={{ fontSize: 13, color: '#555' }}>{bagProducts.length} selected</span>
                        {bagProducts.length > 0 && (
                          <button type="button" onClick={() => setBagProducts([])}
                            style={{ background: 'none', border: 'none', color: '#8B2635', cursor: 'pointer', padding: 0, fontSize: 13 }}>
                            Clear selection
                          </button>
                        )}
                      </div>

                      {/* Pinned: currently-checked products stay visible even when a
                          search filters them out of the list below. */}
                      {selectedBagProducts.length > 0 && (
                        <div style={{ maxHeight: 96, overflowY: 'auto', border: '1px solid #cdddc9', background: '#f2f8f2', borderRadius: 6, padding: 6, marginBottom: 6 }}>
                          {selectedBagProducts.map((p) => (
                            <label key={p.id} style={{ display: 'block', fontSize: 14 }}>
                              <input type="checkbox" checked onChange={() => toggleBagProduct(p.id)} />{' '}
                              {p.name}{p.sku ? <span style={{ color: '#999' }}> ({p.sku})</span> : null}
                            </label>
                          ))}
                        </div>
                      )}

                      <input type="text" placeholder="Search products by name or SKU…"
                        value={bagSearch.value} onChange={(e) => bagSearch.setValue(e.target.value)}
                        style={{ width: '100%', marginBottom: 6 }} />

                      <div style={{ maxHeight: 140, overflowY: 'auto', border: '1px solid #ddd', borderRadius: 6, padding: 8 }}>
                        {unselectedBagMatches.length === 0 ? (
                          <div style={{ color: '#888', fontSize: 13 }}>
                            {products.length === 0 ? 'No products loaded.' : 'No products match your search.'}
                          </div>
                        ) : (
                          unselectedBagMatches.map((p) => (
                            <label key={p.id} style={{ display: 'block', fontSize: 14 }}>
                              <input type="checkbox" checked={false} onChange={() => toggleBagProduct(p.id)} />{' '}
                              {p.name}{p.sku ? <span style={{ color: '#999' }}> ({p.sku})</span> : null}
                            </label>
                          ))
                        )}
                      </div>
                    </div>
                  )}

                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <input type="number" placeholder="Granted bags" value={bagGranted}
                      onChange={(e) => setBagGranted(e.target.value)} style={{ width: 130 }} />
                    <input type="number" placeholder="Already pulled (0)" value={bagAlreadyPulled}
                      onChange={(e) => setBagAlreadyPulled(e.target.value)} style={{ width: 160 }}
                      title="Starting already-pulled count (e.g. grant 40, 4 already pulled → 36 remaining)" />
                  </div>
                  <input type="text" placeholder="Note (optional)" value={note}
                    onChange={(e) => setNote(e.target.value)} />
                  <div>
                    <button disabled={submitting} onClick={submitBag}>Grant</button>
                  </div>
                </div>
              )}

              {/* Ledger history */}
              <div style={{ fontWeight: 600, margin: '16px 0 6px' }}>Ledger history</div>
              {data.ledger.length === 0 ? (
                <div style={{ color: '#888', fontSize: 14 }}>No entries yet.</div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table className="data-table" style={{ width: '100%', fontSize: 14 }}>
                    <thead>
                      <tr>
                        <th>Date</th><th>Type</th><th>Amount / Qty</th><th>Note</th><th>By</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.ledger.map((e) => (
                        <tr key={e.id}>
                          <td>{fmtDate(e.created_at)}</td>
                          <td>{e.entry_type_display}</td>
                          <td>{e.amount !== null ? `$${e.amount}` : `${e.quantity} bag(s)`}</td>
                          <td>{e.note || '—'}</td>
                          <td>{e.created_by_name || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          ) : null}
        </div>

        <div className="modal-footer">
          <button className="modal-close-btn" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
};

export default MemberAccountModal;
